import { useCallback, useState } from 'react'

import {
  type MarkingScheme,
  addMarkingScheme,
  changeMarkingScheme,
  fetchSchemesOfSubject,
  removeMarkingScheme,
} from '../../api/exams'
import { asFailure, type Failure } from '../../api/request'
import { useLoad } from '../../api/useLoad'
import { Button } from '../../ui/Control'
import { useSaving } from '../../ui/useSaving'
import { Failed, Waiting } from '../../ui/Status'
import { RichText } from '../../ui/RichText'
import { MARKDOWN } from '../../ui/markup'

/**
 * How this subject's work is marked.
 *
 * **A marking scheme lives at the subject and nowhere else**, and that placement is the design of the
 * whole area. "Write an opinion" and "draw a family tree" are marked by completely different
 * standards and both happen in one subject — so the standard cannot hang off a test, and every task
 * of every test picks one from here.
 *
 * The description is the substance and not a caption: it says what matters in that kind of task,
 * which sources are to be considered, and **on what scale it is marked**. It is the text an assistant
 * is handed when it is asked to judge an answer, and MyLib itself never reads a word of it.
 *
 * A scheme a task is still measured by cannot be deleted, and the refusal says so. Taking the
 * standard out from under an answer would change, quietly, what that answer was measured by.
 */
export interface MarkingSchemesProps {
  readonly subjectId: string
}

export function MarkingSchemes({ subjectId }: MarkingSchemesProps) {
  const schemes = useLoad(
    useCallback((signal: AbortSignal) => fetchSchemesOfSubject(subjectId, signal), [subjectId]),
  )

  const [writing, setWriting] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [markup, setMarkup] = useState(MARKDOWN)
  const [failure, setFailure] = useState<Failure | null>(null)

  const held: readonly MarkingScheme[] = schemes.load.status === 'ready' ? schemes.load.value : []

  const attempt = (work: () => Promise<void>) => {
    void (async () => {
      try {
        setFailure(null)
        await work()
        schemes.retry()
      } catch (error) {
        setFailure(asFailure(error))
      }
    })()
  }

  return (
    <section className="subject-detail__part" aria-label="How work in this subject is marked">
      <h3 className="subject-detail__part-title">Marking schemes</h3>
      <p className="schemes__lead">
        What matters in one kind of task, which sources count, and on what scale it is marked. A
        task in any test can be measured by one of these.
      </p>

      {schemes.load.status === 'waiting' && <Waiting label="Fetching the marking schemes" />}
      {schemes.load.status === 'failed' && (
        <Failed failure={schemes.load.failure} onRetry={schemes.retry} />
      )}
      {failure !== null && <Failed failure={failure} />}

      {held.length > 0 && (
        <ul className="schemes">
          {held.map((scheme) => (
            <SchemeRow
              key={scheme.id}
              scheme={scheme}
              onChange={(wantedName, wantedDescription, wantedMarkup) => {
                attempt(async () => {
                  await changeMarkingScheme(scheme.id, wantedName, wantedDescription, wantedMarkup)
                })
              }}
              onRemove={() => {
                attempt(async () => {
                  await removeMarkingScheme(scheme.id)
                })
              }}
            />
          ))}
        </ul>
      )}

      {writing ? (
        <div className="scheme scheme--new">
          <input
            className="scheme__name"
            value={name}
            placeholder="Prozessgutachten"
            aria-label="What this marking scheme is called"
            onChange={(event) => {
              setName(event.target.value)
            }}
          />
          <RichText
            compact
            text={description}
            format={markup}
            label="What this marking scheme measures"
            purpose="marking-scheme"
            initially="text"
            placeholder="What matters, which sources count, what the scale is…"
            onChange={setDescription}
            onFormat={(wanted) => {
              setMarkup(wanted)
              return Promise.resolve()
            }}
          />
          <div className="controls">
            <Button
              tone="primary"
              disabled={name.trim() === ''}
              onClick={() => {
                attempt(async () => {
                  await addMarkingScheme(subjectId, name, description, markup)
                  setName('')
                  setDescription('')
                  setWriting(false)
                })
              }}
            >
              Keep it
            </Button>
            <Button
              onClick={() => {
                setWriting(false)
              }}
            >
              Leave it
            </Button>
          </div>
        </div>
      ) : (
        <div className="controls">
          <Button
            icon="plus"
            onClick={() => {
              setWriting(true)
            }}
          >
            A marking scheme
          </Button>
        </div>
      )}
    </section>
  )
}

/**
 * One scheme, written into where it stands.
 *
 * Its own component because of the saving: what is typed is held here and written down a moment
 * after the typing stops, exactly as everything else written in MyLib is. A field that sent a request
 * per keystroke would send one per keystroke to a text that is several paragraphs long.
 */
interface SchemeRowProps {
  readonly scheme: MarkingScheme
  readonly onChange: (name: string, description: string, descriptionMarkup: string) => void
  readonly onRemove: () => void
}

function SchemeRow({ scheme, onChange, onRemove }: SchemeRowProps) {
  const [name, setName] = useState(scheme.name)
  const [description, setDescription] = useState(scheme.description)
  const [markup, setMarkup] = useState(scheme.descriptionMarkup)

  // The two fields are one row and are saved as one, so they travel through the shared saver as a
  // pair: whichever was typed in last carries the other's current value with it.
  const saving = useSaving((both: string) => {
    const wanted = JSON.parse(both) as { name: string; description: string; markup: string }
    onChange(wanted.name, wanted.description, wanted.markup)
    return Promise.resolve()
  })

  const write = (wantedName: string, wantedDescription: string, wantedMarkup = markup) => {
    setName(wantedName)
    setDescription(wantedDescription)
    setMarkup(wantedMarkup)
    saving.typed(
      JSON.stringify({ name: wantedName, description: wantedDescription, markup: wantedMarkup }),
    )
  }

  return (
    <li className="scheme">
      <input
        className="scheme__name"
        value={name}
        aria-label="What this marking scheme is called"
        onChange={(event) => {
          write(event.target.value, description)
        }}
      />
      <RichText
        compact
        text={description}
        format={markup}
        label={`What ${scheme.name} measures`}
        purpose="marking-scheme"
        initially="text"
        placeholder="What matters, which sources count, what the scale is…"
        onChange={(wanted) => {
          write(name, wanted)
        }}
        onFormat={(wanted) => {
          write(name, description, wanted)
          return Promise.resolve()
        }}
      />
      <div className="controls">
        <Button disabled={scheme.inUse} onClick={onRemove}>
          Delete
        </Button>
        {scheme.inUse && <span className="scheme__note">A task is measured by this one.</span>}
      </div>
    </li>
  )
}
